import Phaser from 'phaser';
import {
  CELL_SIZE,
  FAMILY_COLORS,
  Family,
  Tier,
} from '../config';
import { ELEMENT_DEFS } from '../data/elements';
import { TEXT_PRIMARY } from '../ui/theme';

/**
 * BootScene: Generates placeholder graphics for all game elements.
 *
 * Shape assignment mirrors design/handoff/DESIGN_TOKENS.md:
 * - Fire: Upward-pointing triangle
 * - Water: Teardrop
 * - Earth: Flat-top hexagon
 * - Air: Diamond (rotated square)
 * - Lightning: 4-point star (sharp sparkle)
 * - Shadow: Octagon
 * - Light: Circle
 * - Void: Square-45 (rotated square, broader than Air's diamond)
 * - Wild: 5-point star (off-ladder, visually distinct from the 8 ladder families)
 *
 * **Tier silhouettes (review fix #3):** Tier is encoded into the *geometry*
 * itself, not just alpha + tiny pips. Players should be able to identify a
 * tier by silhouette alone — even at a glance, even in motion. The old
 * 1.5px pip dots were unreadable on a phone; they're gone.
 *
 * - Fragment (T1): Solo shape, no inner detail.
 * - Shard    (T2): Shape + small inner shape (echo).
 * - Crystal  (T3): Shape with hollow center (inner cutout).
 * - Gem      (T4): Shape with bright concentric inner shape (gem core).
 * - Core     (T5): Shape with single concentric outer ring (halo).
 * - Primal   (T6): Shape with double concentric rings.
 * - Eternal  (T7): Shape with triple concentric rings + glow.
 *
 * Combined with stronger alpha progression, tiers are distinguishable by
 * silhouette alone — meeting the "blurred grayscale" readability test.
 */
export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'Boot' });
  }

  preload(): void {
    // Generate textures for all elements
    this.generateElementTextures();

    // Generate UI element textures
    this.generateUITextures();
  }

  create(): void {
    // Pre-warm the Vignette post-FX shader pipeline.
    //
    // GameScene.create() calls cameras.main.postFX.addVignette() to install
    // the corruption vignette. The first time that pipeline is used the WebGL
    // shader compiles synchronously on the GPU, costing several hundred ms on
    // a cold renderer. Doing it here — while the player is already looking at
    // a "loading" state and waiting on fonts — moves that cost out of the
    // click→tutorial path.
    //
    // We attach the vignette to BootScene's own camera, give the renderer a
    // single frame to render with it (which forces the pipeline init), then
    // detach. The compiled shader stays cached for the renderer's lifetime,
    // so GameScene's later addVignette() is a no-op cost.
    let warmupVignette: Phaser.FX.Vignette | null = null;
    if (this.game.renderer.type === Phaser.WEBGL) {
      warmupVignette = this.cameras.main.postFX.addVignette(0.5, 0.5, 1, 0);
    }

    // Wait for: (a) WebFonts to load (prevents an Arial flash on first text
    // render) AND (b) one frame so the warmup vignette actually renders.
    // document.fonts.ready resolves immediately if fonts are already loaded.
    const fontsReady: Promise<unknown> =
      typeof document !== 'undefined' && document.fonts && document.fonts.ready
        ? document.fonts.ready
        : Promise.resolve();

    const oneFrame = new Promise<void>((resolve) => {
      this.time.delayedCall(16, () => resolve());
    });

    Promise.all([fontsReady, oneFrame]).then(() => {
      if (warmupVignette) {
        this.cameras.main.postFX.remove(warmupVignette);
      }
      this.scene.start('Menu');
    });
  }

  private generateElementTextures(): void {
    const gfx = this.add.graphics({ x: 0, y: 0 });

    // Tier brightness progression — bumped vs. previous values so even Fragment
    // is visible at a glance. The old 0.40 alpha was washing out on dark
    // backgrounds, especially in motion.
    const TIER_ALPHA: Record<number, number> = {
      [Tier.Fragment]: 0.62,
      [Tier.Shard]:    0.72,
      [Tier.Crystal]:  0.82,
      [Tier.Gem]:      0.90,
      [Tier.Core]:     0.95,
      [Tier.Primal]:   1.00,
      [Tier.Eternal]:  1.00,
    };

    for (const elementDef of ELEMENT_DEFS) {
      const family = elementDef.family;
      const tier = elementDef.tier;
      const color = FAMILY_COLORS[family];
      const fillAlpha = TIER_ALPHA[tier];

      // Determine texture size and base radius based on tier
      let textureWidth = 64;
      let textureHeight = 64;
      let centerX = 32;
      let centerY = 32;
      let baseRadius = 10;

      // Fragment (T1): Solo shape, no inner detail.
      if (tier === Tier.Fragment) {
        baseRadius = 12;
        gfx.clear();
        gfx.fillStyle(color, fillAlpha);
        this.drawFamilyShape(gfx, centerX, centerY, baseRadius, family);
        gfx.generateTexture(elementDef.id, textureWidth, textureHeight);
      }
      // Shard (T2): Shape + small inner echo of the same shape.
      else if (tier === Tier.Shard) {
        baseRadius = 15;
        gfx.clear();
        gfx.fillStyle(color, fillAlpha);
        this.drawFamilyShape(gfx, centerX, centerY, baseRadius, family);
        // Inner echo (35% size, brighter) — makes the silhouette read as "stacked"
        gfx.fillStyle(0xF5F7FA, 0.35);
        this.drawFamilyShape(gfx, centerX, centerY, baseRadius * 0.35, family);
        gfx.generateTexture(elementDef.id, textureWidth, textureHeight);
      }
      // Crystal (T3): Shape with hollow center (cutout via dark fill).
      else if (tier === Tier.Crystal) {
        baseRadius = 17;
        gfx.clear();
        gfx.fillStyle(color, fillAlpha);
        this.drawFamilyShape(gfx, centerX, centerY, baseRadius, family);
        gfx.lineStyle(2, color, 1.0);
        this.drawFamilyShapeOutline(gfx, centerX, centerY, baseRadius, family);
        // Hollow center (dark cutout) — silhouette breaks the solid fill
        gfx.fillStyle(0x050507, 1.0);
        this.drawFamilyShape(gfx, centerX, centerY, baseRadius * 0.42, family);
        gfx.generateTexture(elementDef.id, textureWidth, textureHeight);
      }
      // Gem (T4): Shape with bright concentric inner gem core.
      else if (tier === Tier.Gem) {
        baseRadius = 19;
        gfx.clear();
        gfx.fillStyle(color, fillAlpha);
        this.drawFamilyShape(gfx, centerX, centerY, baseRadius, family);
        gfx.lineStyle(2, color, 1.0);
        this.drawFamilyShapeOutline(gfx, centerX, centerY, baseRadius, family);
        // Inner bright core — same shape, smaller, near-white tint
        gfx.fillStyle(TEXT_PRIMARY, 0.55);
        this.drawFamilyShape(gfx, centerX, centerY, baseRadius * 0.5, family);
        gfx.generateTexture(elementDef.id, textureWidth, textureHeight);
      }
      // Core (T5): Shape with single concentric outer ring (halo).
      else if (tier === Tier.Core) {
        baseRadius = 22;
        gfx.clear();
        // Outer halo ring
        gfx.lineStyle(3, color, 0.7);
        gfx.strokeCircle(centerX, centerY, baseRadius + 7);
        // Main shape
        gfx.fillStyle(color, fillAlpha);
        this.drawFamilyShape(gfx, centerX, centerY, baseRadius, family);
        gfx.lineStyle(2, color, 1.0);
        this.drawFamilyShapeOutline(gfx, centerX, centerY, baseRadius, family);
        // Bright inner core
        gfx.fillStyle(TEXT_PRIMARY, 0.5);
        this.drawFamilyShape(gfx, centerX, centerY, baseRadius * 0.4, family);
        gfx.generateTexture(elementDef.id, textureWidth, textureHeight);
      }
      // Primal (T6): Shape with double concentric rings.
      else if (tier === Tier.Primal) {
        textureWidth = 80;
        textureHeight = 80;
        centerX = 40;
        centerY = 40;
        baseRadius = 25;
        gfx.clear();
        // Outer ring
        gfx.lineStyle(2, color, 0.55);
        gfx.strokeCircle(centerX, centerY, baseRadius + 12);
        // Inner ring
        gfx.lineStyle(2.5, color, 0.85);
        gfx.strokeCircle(centerX, centerY, baseRadius + 5);
        // Main shape
        gfx.fillStyle(color, fillAlpha);
        this.drawFamilyShape(gfx, centerX, centerY, baseRadius, family);
        gfx.lineStyle(2.5, color, 1.0);
        this.drawFamilyShapeOutline(gfx, centerX, centerY, baseRadius, family);
        // Bright inner core
        gfx.fillStyle(TEXT_PRIMARY, 0.55);
        this.drawFamilyShape(gfx, centerX, centerY, baseRadius * 0.42, family);
        gfx.generateTexture(elementDef.id, textureWidth, textureHeight);
      }
      // Eternal (T7): Shape with triple concentric rings + glow.
      else if (tier === Tier.Eternal) {
        textureWidth = 96;
        textureHeight = 96;
        centerX = 48;
        centerY = 48;
        baseRadius = 28;
        gfx.clear();
        // Triple halo rings
        gfx.lineStyle(2, color, 0.45);
        gfx.strokeCircle(centerX, centerY, baseRadius + 16);
        gfx.lineStyle(2.5, color, 0.7);
        gfx.strokeCircle(centerX, centerY, baseRadius + 9);
        gfx.lineStyle(3, color, 1.0);
        gfx.strokeCircle(centerX, centerY, baseRadius + 3);
        // Main shape
        gfx.fillStyle(color, fillAlpha);
        this.drawFamilyShape(gfx, centerX, centerY, baseRadius, family);
        gfx.lineStyle(3, color, 1.0);
        this.drawFamilyShapeOutline(gfx, centerX, centerY, baseRadius, family);
        // Bright inner core
        gfx.fillStyle(TEXT_PRIMARY, 0.6);
        this.drawFamilyShape(gfx, centerX, centerY, baseRadius * 0.42, family);
        gfx.generateTexture(elementDef.id, textureWidth, textureHeight);
      }
    }

    gfx.destroy();
  }

  /**
   * Draw the primary filled shape for a given family.
   */
  private drawFamilyShape(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number,
    family: Family
  ): void {
    switch (family) {
      case Family.Fire:
        this.drawTriangle(gfx, x, y, radius); // Upward-pointing triangle
        break;
      case Family.Water:
        this.drawTeardrop(gfx, x, y, radius); // Teardrop/droplet
        break;
      case Family.Earth:
        this.drawHexagon(gfx, x, y, radius); // Flat-top hexagon
        break;
      case Family.Air:
        this.drawDiamond(gfx, x, y, radius); // Diamond (rotated square)
        break;
      case Family.Lightning:
        this.drawFourPointedStar(gfx, x, y, radius); // 4-point sparkle star
        break;
      case Family.Shadow:
        this.drawOctagon(gfx, x, y, radius); // Octagon
        break;
      case Family.Light:
        this.drawCircle(gfx, x, y, radius); // Circle (pure light)
        break;
      case Family.Void:
        this.drawSquare45(gfx, x, y, radius); // Square-45 (broader rotated square)
        break;
      case Family.Wild:
        this.drawFivePointStar(gfx, x, y, radius); // 5-point star (off-ladder)
        break;
      default:
        // Fallback to circle
        gfx.fillCircle(x, y, radius);
    }
  }

  /**
   * Draw just the outline of a shape for a given family.
   * Used to add visual definition to larger tiers.
   */
  private drawFamilyShapeOutline(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number,
    family: Family
  ): void {
    switch (family) {
      case Family.Fire:
        this.drawTriangleOutline(gfx, x, y, radius);
        break;
      case Family.Water:
        this.drawTeardropOutline(gfx, x, y, radius);
        break;
      case Family.Earth:
        this.drawHexagonOutline(gfx, x, y, radius);
        break;
      case Family.Air:
        this.drawDiamondOutline(gfx, x, y, radius);
        break;
      case Family.Lightning:
        this.drawFourPointedStarOutline(gfx, x, y, radius);
        break;
      case Family.Shadow:
        this.drawOctagonOutline(gfx, x, y, radius);
        break;
      case Family.Light:
        this.drawCircleOutline(gfx, x, y, radius);
        break;
      case Family.Void:
        this.drawSquare45Outline(gfx, x, y, radius);
        break;
      case Family.Wild:
        this.drawFivePointStarOutline(gfx, x, y, radius);
        break;
    }
  }

  // ========== SHAPE DRAWING METHODS ==========

  // Fire: Upward-pointing triangle (flame)
  private drawTriangle(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    gfx.beginPath();
    gfx.moveTo(x, y - radius);
    gfx.lineTo(x + radius, y + radius);
    gfx.lineTo(x - radius, y + radius);
    gfx.closePath();
    gfx.fillPath();
  }

  private drawTriangleOutline(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    gfx.beginPath();
    gfx.moveTo(x, y - radius);
    gfx.lineTo(x + radius, y + radius);
    gfx.lineTo(x - radius, y + radius);
    gfx.closePath();
    gfx.strokePath();
  }

  // Water: Teardrop/droplet shape
  private drawTeardrop(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    // Approximate teardrop as a circle on top + triangle point at bottom
    gfx.beginPath();
    gfx.arc(x, y - radius * 0.3, radius * 0.7, Math.PI, 2 * Math.PI, false);
    gfx.lineTo(x + radius * 0.6, y + radius * 0.6);
    gfx.lineTo(x, y + radius); // sharp point
    gfx.lineTo(x - radius * 0.6, y + radius * 0.6);
    gfx.closePath();
    gfx.fillPath();
  }

  private drawTeardropOutline(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    gfx.beginPath();
    gfx.arc(x, y - radius * 0.3, radius * 0.7, Math.PI, 2 * Math.PI, false);
    gfx.lineTo(x + radius * 0.6, y + radius * 0.6);
    gfx.lineTo(x, y + radius);
    gfx.lineTo(x - radius * 0.6, y + radius * 0.6);
    gfx.closePath();
    gfx.strokePath();
  }

  // Earth: Hexagon
  private drawHexagon(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    gfx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (i * 60 - 90) * (Math.PI / 180);
      const px = x + radius * Math.cos(angle);
      const py = y + radius * Math.sin(angle);
      if (i === 0) gfx.moveTo(px, py);
      else gfx.lineTo(px, py);
    }
    gfx.closePath();
    gfx.fillPath();
  }

  private drawHexagonOutline(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    gfx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (i * 60 - 90) * (Math.PI / 180);
      const px = x + radius * Math.cos(angle);
      const py = y + radius * Math.sin(angle);
      if (i === 0) {
        gfx.moveTo(px, py);
      } else {
        gfx.lineTo(px, py);
      }
    }
    gfx.closePath();
    gfx.strokePath();
  }

  // Light: Circle (purest light silhouette)
  private drawCircle(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    gfx.fillCircle(x, y, radius);
  }

  private drawCircleOutline(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    gfx.strokeCircle(x, y, radius);
  }

  // Void: Square-45 (rotated square, broader than Air's diamond — square-root-2 bbox)
  private drawSquare45(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    const r = radius * 0.95; // slightly smaller so corners don't clip bbox
    gfx.beginPath();
    gfx.moveTo(x, y - r);
    gfx.lineTo(x + r, y);
    gfx.lineTo(x, y + r);
    gfx.lineTo(x - r, y);
    gfx.closePath();
    gfx.fillPath();
  }

  private drawSquare45Outline(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    const r = radius * 0.95;
    gfx.beginPath();
    gfx.moveTo(x, y - r);
    gfx.lineTo(x + r, y);
    gfx.lineTo(x, y + r);
    gfx.lineTo(x - r, y);
    gfx.closePath();
    gfx.strokePath();
  }

  // Wild: 5-point star (off-ladder silhouette, visually distinct from the 8 ladder families)
  private drawFivePointStar(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    this.buildFivePointStarPath(gfx, x, y, radius);
    gfx.fillPath();
  }

  private drawFivePointStarOutline(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    this.buildFivePointStarPath(gfx, x, y, radius);
    gfx.strokePath();
  }

  private buildFivePointStarPath(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    const inner = radius * 0.45;
    gfx.beginPath();
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? radius : inner;
      const angle = (i * 36 - 90) * (Math.PI / 180);
      const px = x + r * Math.cos(angle);
      const py = y + r * Math.sin(angle);
      if (i === 0) gfx.moveTo(px, py);
      else gfx.lineTo(px, py);
    }
    gfx.closePath();
  }

  // Lightning: 4-pointed star (sharp sparkle)
  private drawFourPointedStar(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    gfx.beginPath();
    gfx.moveTo(x, y - radius);
    gfx.lineTo(x + radius * 0.35, y - radius * 0.35);
    gfx.lineTo(x + radius, y);
    gfx.lineTo(x + radius * 0.35, y + radius * 0.35);
    gfx.lineTo(x, y + radius);
    gfx.lineTo(x - radius * 0.35, y + radius * 0.35);
    gfx.lineTo(x - radius, y);
    gfx.lineTo(x - radius * 0.35, y - radius * 0.35);
    gfx.closePath();
    gfx.fillPath();
  }

  private drawFourPointedStarOutline(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    gfx.beginPath();
    gfx.moveTo(x, y - radius);
    gfx.lineTo(x + radius * 0.35, y - radius * 0.35);
    gfx.lineTo(x + radius, y);
    gfx.lineTo(x + radius * 0.35, y + radius * 0.35);
    gfx.lineTo(x, y + radius);
    gfx.lineTo(x - radius * 0.35, y + radius * 0.35);
    gfx.lineTo(x - radius, y);
    gfx.lineTo(x - radius * 0.35, y - radius * 0.35);
    gfx.closePath();
    gfx.strokePath();
  }

  // Shadow: Octagon
  private drawOctagon(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    gfx.beginPath();
    for (let i = 0; i < 8; i++) {
      const angle = (i * 45 - 90) * (Math.PI / 180);
      const px = x + radius * Math.cos(angle);
      const py = y + radius * Math.sin(angle);
      if (i === 0) gfx.moveTo(px, py);
      else gfx.lineTo(px, py);
    }
    gfx.closePath();
    gfx.fillPath();
  }

  private drawOctagonOutline(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    gfx.beginPath();
    for (let i = 0; i < 8; i++) {
      const angle = (i * 45 - 90) * (Math.PI / 180);
      const px = x + radius * Math.cos(angle);
      const py = y + radius * Math.sin(angle);
      if (i === 0) {
        gfx.moveTo(px, py);
      } else {
        gfx.lineTo(px, py);
      }
    }
    gfx.closePath();
    gfx.strokePath();
  }

  // Air: Diamond/rhombus (rotated square)
  private drawDiamond(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    gfx.beginPath();
    gfx.moveTo(x, y - radius);       // Top
    gfx.lineTo(x + radius, y);       // Right
    gfx.lineTo(x, y + radius);       // Bottom
    gfx.lineTo(x - radius, y);       // Left
    gfx.closePath();
    gfx.fillPath();
  }

  private drawDiamondOutline(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number
  ): void {
    gfx.beginPath();
    gfx.moveTo(x, y - radius);
    gfx.lineTo(x + radius, y);
    gfx.lineTo(x, y + radius);
    gfx.lineTo(x - radius, y);
    gfx.closePath();
    gfx.strokePath();
  }

  // ========== UI TEXTURE GENERATION ==========

  private generateUITextures(): void {
    const gfx = this.add.graphics({ x: 0, y: 0 });

    // Cell background: rounded rectangle — bg-cell token (VISUAL-IDENTITY.md Section 5.3)
    gfx.clear();
    gfx.fillStyle(0x0F1115, 1); // BG_CELL token
    const cellSize = CELL_SIZE;
    const radius = 10; // 10px corner radius (Section 5.3: "slightly more rounded than 8px")
    this.drawRoundedRect(gfx, 0, 0, cellSize, cellSize, radius);
    gfx.generateTexture('cell_bg', cellSize, cellSize);

    // Splinter button: accent-premium rounded rectangle (VISUAL-IDENTITY.md Section 3.3 — accent-premium token)
    gfx.clear();
    gfx.fillStyle(0x8A00FF, 1); // ACCENT_PURPLE — premium/overcharge-ready
    const btnWidth = 60;
    const btnHeight = 40;
    this.drawRoundedRect(gfx, 0, 0, btnWidth, btnHeight, 6);
    gfx.generateTexture('btn_splinter', btnWidth, btnHeight);

    gfx.destroy();
  }

  private drawRoundedRect(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ): void {
    gfx.beginPath();
    gfx.moveTo(x + radius, y);
    gfx.lineTo(x + width - radius, y);
    // Use arc to draw rounded corner
    gfx.arc(x + width - radius, y + radius, radius, -Math.PI / 2, 0, false);
    gfx.lineTo(x + width, y + height - radius);
    gfx.arc(x + width - radius, y + height - radius, radius, 0, Math.PI / 2, false);
    gfx.lineTo(x + radius, y + height);
    gfx.arc(x + radius, y + height - radius, radius, Math.PI / 2, Math.PI, false);
    gfx.lineTo(x, y + radius);
    gfx.arc(x + radius, y + radius, radius, Math.PI, (3 * Math.PI) / 2, false);
    gfx.closePath();
    gfx.fillPath();
  }
}
